// Phase T2.0A Prompt 2.2 — Admin Alternates Management.
//
// FIFO waitlist admin panel: promote or remove alternates. Promotion is
// atomic: the backend re-checks capacity under the event-scoped advisory
// lock and either succeeds cleanly or returns 409 capacity_full. We surface
// server errors as toasts and reload the parent on success.
//
// Also displays a compact "capacity: X / Y" summary so the admin sees at a
// glance whether promotion is even possible.

import React, { useState } from 'react';
import { CaretDoubleUp, X, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MemberLink from '../MemberLink';
import { alternatesAPI } from '../../lib/alternatesApi';
import { registeredPlayerCount, playerCapacity } from '../../lib/capacity';
import ConfirmDialog from './ConfirmDialog';

export default function AlternatesManager({
  eventKind,   // 'tournament' | 'round_robin'
  event,
  onChanged,
}) {
  const [busy, setBusy] = useState(null);   // profile_id currently mutating
  const [removeTarget, setRemoveTarget] = useState(null);
  const [promoteTarget, setPromoteTarget] = useState(null);

  const eventType = eventKind === 'tournament' ? event.tournament_type : event.rr_type;
  const cap = playerCapacity(eventType, event.max_entries);
  const current = registeredPlayerCount(event.entries || []);
  const spotsLeft = cap == null ? Infinity : Math.max(0, cap - current);
  const alternates = event.alternates || [];

  async function promote(profileId) {
    setBusy(profileId);
    try {
      await alternatesAPI.promote(eventKind, event.id, profileId);
      toast.success('Alternate promoted to registered.');
      setPromoteTarget(null);
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not promote alternate.'));
    } finally {
      setBusy(null);
    }
  }

  async function remove(profileId) {
    setBusy(profileId);
    try {
      await alternatesAPI.remove(eventKind, event.id, profileId);
      toast.success('Alternate removed.');
      setRemoveTarget(null);
      await onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not remove alternate.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="admin-alternates">
      <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-[#013523]" />
          <div className="text-sm font-medium text-[#013523]">Alternates ({alternates.length})</div>
        </div>
        <div className="text-[11px] text-[#6a766e]" data-testid="admin-alternates-capacity">
          {cap == null
            ? 'No capacity limit configured.'
            : `Capacity: ${current} / ${cap} players · ${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} available`}
        </div>
      </div>

      {alternates.length === 0 ? (
        <div className="text-sm text-[#6a766e]" data-testid="admin-alternates-empty">
          No members on the alternate list.
        </div>
      ) : (
        <ul className="divide-y divide-[#f3f1ec]">
          {alternates.map((a) => {
            const isBusy = busy === a.profile_id;
            const canPromote = spotsLeft > 0;
            return (
              <li
                key={a.id}
                data-testid={`admin-alternate-row-${a.profile_id}`}
                className="flex items-center gap-3 py-2.5"
              >
                <div className="shrink-0 w-7 h-7 rounded-full bg-[#fbfaf8] border border-[#e3dcd1] flex items-center justify-center text-[11px] font-semibold text-[#013523]">
                  #{a.position}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-[#013523] font-medium truncate">
                    <MemberLink profileId={a.profile_id} name={a.profile?.name} />
                  </div>
                  <div className="text-[10.5px] text-[#9aa39d]">
                    Joined {new Date(a.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setPromoteTarget(a)}
                    disabled={isBusy || !canPromote}
                    title={canPromote ? 'Promote to registered' : 'No spots available'}
                    data-testid={`admin-alt-promote-${a.profile_id}`}
                    className="text-[11px] px-2 py-1 rounded-full bg-[#013523] text-white flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <CaretDoubleUp size={11} weight="bold" /> Promote
                  </button>
                  <button
                    type="button"
                    onClick={() => setRemoveTarget(a)}
                    disabled={isBusy}
                    data-testid={`admin-alt-remove-${a.profile_id}`}
                    title="Remove from alternate list"
                    className="text-[#6a766e] hover:text-red-700 disabled:opacity-40"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!promoteTarget}
        onOpenChange={(v) => !v && setPromoteTarget(null)}
        title="Promote alternate"
        description={promoteTarget ? `Promote ${promoteTarget.profile?.name || 'this alternate'} to registered? Capacity will be re-checked atomically.` : ''}
        confirmLabel="Promote"
        onConfirm={() => promote(promoteTarget.profile_id)}
        busy={busy != null}
      />

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(v) => !v && setRemoveTarget(null)}
        title="Remove alternate"
        description={removeTarget ? `Remove ${removeTarget.profile?.name || 'this alternate'} from the waitlist?` : ''}
        confirmLabel="Remove"
        destructive
        onConfirm={() => remove(removeTarget.profile_id)}
        busy={busy != null}
      />
    </div>
  );
}
